import { useEffect, useState } from 'react';
import { FieldRow } from '../../shared';
import styles from './ProgramDetailsSection.module.css';

export interface ProgramDetailsData {
  host_country: string | null;
  language_requirements: string | null;
  submission_type: string | null;
  has_audiobook_categories: boolean | null;
  bisac_default_primary_code: string | null;
  bisac_default_secondary_code: string | null;
  bisac_default_tertiary_code: string | null;
  bisac_preferred_edition_year: string | null;
  industry_warnings: string | null;
  qc_flag_program_details: boolean | null;
  qc_flag_program_details_notes: string | null;
}

export interface ProgramDetailsSectionProps {
  data: ProgramDetailsData;
  editable: boolean;
  onChange?: (updates: Partial<ProgramDetailsData>) => void;
}

export function ProgramDetailsSection({ data, editable, onChange }: ProgramDetailsSectionProps) {
  const [localData, setLocalData] = useState<ProgramDetailsData>(data);

  useEffect(() => {
    setLocalData(data);
  }, [data]);

  const handleFieldChange = (field: keyof ProgramDetailsData, value: string | boolean | null) => {
    setLocalData((prev) => ({
      ...prev,
      [field]: value
    }));
    onChange?.({ [field]: value } as Partial<ProgramDetailsData>);
  };

  const handleBooleanFieldChange = (field: keyof ProgramDetailsData, rawValue: string) => {
    const normalized = rawValue.trim().toLowerCase();
    let parsed: boolean | null = null;
    if (normalized === 'yes' || normalized === 'y' || normalized === 'true') {
      parsed = true;
    } else if (normalized === 'no' || normalized === 'n' || normalized === 'false') {
      parsed = false;
    }
    handleFieldChange(field, parsed);
  };

  return (
    <div className={styles.container}>
      <div className={styles.fieldsGrid}>
        <FieldRow
          label="Host Country"
          value={localData.host_country}
          editable={editable}
          onChange={(v) => handleFieldChange('host_country', v)}
        />
        <FieldRow
          label="Submission Type"
          value={localData.submission_type}
          editable={editable}
          onChange={(v) => handleFieldChange('submission_type', v)}
        />
        <FieldRow
          label="Audiobook Categories"
          value={
            localData.has_audiobook_categories === null
              ? ''
              : localData.has_audiobook_categories
                ? 'Yes'
                : 'No'
          }
          editable={editable}
          onChange={(v) => handleBooleanFieldChange('has_audiobook_categories', v)}
        />
        <FieldRow
          label="BISAC Default (Primary)"
          value={localData.bisac_default_primary_code}
          editable={editable}
          onChange={(v) => handleFieldChange('bisac_default_primary_code', v)}
        />
        <FieldRow
          label="BISAC Default (Secondary)"
          value={localData.bisac_default_secondary_code}
          editable={editable}
          onChange={(v) => handleFieldChange('bisac_default_secondary_code', v)}
        />
        <FieldRow
          label="BISAC Default (Tertiary)"
          value={localData.bisac_default_tertiary_code}
          editable={editable}
          onChange={(v) => handleFieldChange('bisac_default_tertiary_code', v)}
        />
        <FieldRow
          label="Preferred Edition Year"
          value={localData.bisac_preferred_edition_year}
          editable={editable}
          onChange={(v) => handleFieldChange('bisac_preferred_edition_year', v)}
        />
      </div>

      <FieldRow
        label="Language Requirements"
        value={localData.language_requirements}
        editable={editable}
        onChange={(v) => handleFieldChange('language_requirements', v)}
        multiline
        className={styles.fullWidth}
      />

      <FieldRow
        label="Industry Warnings"
        value={localData.industry_warnings}
        editable={editable}
        onChange={(v) => handleFieldChange('industry_warnings', v)}
        multiline
        className={styles.fullWidth}
      />
    </div>
  );
}

export default ProgramDetailsSection;
